import { Box } from '@mui/material';
import { legacyColors } from '@/styles/theme';
import type { MenuPhaseGroup } from '@/store/wizard.store';

interface PhaseTabBarProps {
  phases: MenuPhaseGroup[];
  currentPhaseKey: string | null;
  onSelectPhase: (phaseKey: string) => void;
}

type PhaseStatus = 'active' | 'completed' | 'blocked';

/**
 * Tab-bar delle fasi (LAYOUT · TECNOLOGIA · GIORNO E NOTTE · BAGNO), fedele al legacy
 * (components/Tabs/TabStep.jsx): ribbon angolati con clip-path + pallino di stato
 * (verde = completata, arancio = corrente). Cliccabile per saltare a una fase.
 */
export function PhaseTabBar({ phases, currentPhaseKey, onSelectPhase }: PhaseTabBarProps): JSX.Element {
  const ordered = [...phases].sort((a, b) => a.phaseOrder - b.phaseOrder);

  return (
    <Box sx={{ display: 'flex', gap: 0.5, pr: { md: 2.5 }, mb: 3 }}>
      {ordered.map((ph) => {
        const substeps = ph.rooms.flatMap((r) => r.steps.flatMap((s) => s.substeps));
        const completed = substeps.length > 0 && substeps.every((s) => s.isAnswered);
        const active = ph.phaseKey === currentPhaseKey;
        const status: PhaseStatus = active ? 'active' : completed ? 'completed' : 'blocked';

        const bg =
          status === 'active'
            ? legacyColors.white
            : status === 'completed'
              ? legacyColors.brownGrey3
              : legacyColors.brownGrey2;
        const color =
          status === 'active'
            ? legacyColors.orange
            : status === 'completed'
              ? legacyColors.black
              : legacyColors.grey2;
        const dot =
          status === 'active'
            ? legacyColors.orange
            : status === 'completed'
              ? legacyColors.green
              : legacyColors.white;

        return (
          <Box
            key={ph.phaseId}
            role="button"
            tabIndex={active ? -1 : 0}
            aria-current={active ? 'step' : undefined}
            onClick={() => {
              if (!active) onSelectPhase(ph.phaseKey);
            }}
            onKeyDown={(e) => {
              if (!active && (e.key === 'Enter' || e.key === ' ')) onSelectPhase(ph.phaseKey);
            }}
            sx={{
              flex: 1,
              maxWidth: '20%',
              height: { xs: 50, md: 60 },
              display: 'flex',
              alignItems: 'center',
              pl: 1.5,
              pr: '34px',
              bgcolor: bg,
              color,
              borderRadius: '4px',
              cursor: active ? 'default' : 'pointer',
              userSelect: 'none',
              clipPath: 'polygon(0 0, 90% 0%, 100% 100%, 0% 100%)',
              fontFamily: '"p22-underground", sans-serif',
              fontWeight: 750,
              fontSize: { xs: 13, md: 14 },
              textTransform: 'uppercase',
              letterSpacing: '0.02em',
              lineHeight: 1.1,
              transition: 'background-color 150ms',
            }}
          >
            <Box
              component="span"
              aria-hidden="true"
              sx={{
                width: 15,
                minWidth: 15,
                height: 15,
                mr: 1.5,
                borderRadius: '50%',
                bgcolor: dot,
              }}
            />
            {ph.phaseName}
          </Box>
        );
      })}
    </Box>
  );
}
